import { FormEvent, useEffect, useMemo, useState } from 'react';
import AdminOrchSplitPane from './AdminOrchSplitPane';
import RealmUserPicker from './RealmUserPicker';
import PlatformServicesChecklist from '@frontend/components/projects/PlatformServicesChecklist';
import {
  normalizeProjectServices,
  resolveInstalledProjectServices,
  type ProjectServiceId,
} from '@frontend/components/projects/projectServices';
import CreateProjectOrchestrationWizard from '@frontend/pages/dashboard/admin/ecosystem/components/orchestration/CreateProjectOrchestrationWizard';
import { authClient } from '@frontend/api-clients/authClient';
import { usePublicIntegration } from '@frontend/context/PublicIntegrationContext';
import {
  deleteProjectMembershipV2,
  postProjectMembershipV2,
} from '@frontend/api-clients/adminMembershipClient';
import { fetchProjectCreateScope } from '@frontend/api-clients/adminProjectProvisionClient';
import {
  reconcileAdminProject,
  updateAdminProjectServices,
} from '@frontend/api-clients/projectAccessClient';
import type {
  AdminProjectAccessProject,
  ProjectMember,
} from '@frontend/types';
import type { RealmUserSearchResult } from '@frontend/api-clients/adminUserClient';

function projectionSummary(member: ProjectMember): string {
  const rows = Object.entries(member.projection || {});
  if (rows.length === 0) return 'Not synced';
  if (rows.every(([, value]) => value?.status === 'synced' || value?.status === 'skipped')) {
    return 'Synced';
  }
  return rows.map(([service, value]) => `${service}: ${value?.status || 'unknown'}`).join(' · ');
}

export interface ProjectAccessSplitPanelProps {
  ecosystemId: string;
  projects: AdminProjectAccessProject[];
  loading: boolean;
  focusProjectId?: string | null;
  onReload: () => Promise<void>;
}

export default function ProjectAccessSplitPanel({
  ecosystemId,
  projects,
  loading,
  focusProjectId,
  onReload,
}: ProjectAccessSplitPanelProps) {
  const integration = usePublicIntegration();
  const [selectedProjectId, setSelectedProjectId] = useState(focusProjectId || '');
  const [mode, setMode] = useState<'details' | 'create'>('details');
  const [editorMode, setEditorMode] = useState<'add' | 'reassign' | null>(null);
  const [editingMember, setEditingMember] = useState<ProjectMember | null>(null);
  const [selectedUser, setSelectedUser] = useState<RealmUserSearchResult | null>(null);
  const [userQuery, setUserQuery] = useState('');
  const [role, setRole] = useState('viewer');
  const [pickerKey, setPickerKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [services, setServices] = useState<ProjectServiceId[]>([]);
  const [canCreate, setCanCreate] = useState(false);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) || null,
    [projects, selectedProjectId],
  );

  useEffect(() => {
    if (focusProjectId && projects.some((project) => project.id === focusProjectId)) {
      setSelectedProjectId(focusProjectId);
      setMode('details');
      return;
    }
    if (!selectedProjectId || !projects.some((project) => project.id === selectedProjectId)) {
      setSelectedProjectId(projects[0]?.id || '');
    }
  }, [focusProjectId, projects, selectedProjectId]);

  useEffect(() => {
    setServices(normalizeProjectServices(selectedProject?.services));
    setEditorMode(null);
    setEditingMember(null);
    setError('');
    setMessage('');
  }, [selectedProject]);

  useEffect(() => {
    const token = authClient.getToken();
    if (!token) return;
    void fetchProjectCreateScope(token)
      .then((scope) => {
        const ecosystemAllowed = scope.ecosystems.some((row) => row.id === ecosystemId);
        const parentAllowed = scope.parentProjects.some(
          (row) => row.ecosystemId === ecosystemId,
        );
        const allowed = Boolean(scope.eligible && (ecosystemAllowed || parentAllowed));
        setCanCreate(allowed);
      })
      .catch(() => setCanCreate(false));
  }, [ecosystemId]);

  const beginAdd = () => {
    setEditorMode('add');
    setEditingMember(null);
    setSelectedUser(null);
    setUserQuery('');
    setRole(selectedProject?.capabilities?.grantableRoles?.[0] || 'viewer');
    setPickerKey((value) => value + 1);
    setError('');
  };

  const beginReassign = (member: ProjectMember) => {
    setEditorMode('reassign');
    setEditingMember(member);
    setSelectedUser({
      userId: member.userPk,
      username: member.username,
      email: member.email,
    });
    setUserQuery(member.email);
    setRole(member.role || 'viewer');
    setPickerKey((value) => value + 1);
    setError('');
  };

  const saveAssignment = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedProject) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const token = authClient.getToken();
      if (!token) throw new Error('You must be signed in.');
      const email = String(selectedUser?.email || userQuery).trim().toLowerCase();
      if (!email.includes('@')) {
        throw new Error('Select a realm user or enter a complete email address.');
      }
      await postProjectMembershipV2(token, {
        projectId: selectedProject.id,
        email,
        role,
        ensureEcosystemMembership: true,
        syncDownstream: true,
      });
      if (
        editorMode === 'reassign' &&
        editingMember &&
        selectedUser?.userId &&
        selectedUser.userId !== editingMember.userPk
      ) {
        await deleteProjectMembershipV2(token, {
          projectId: selectedProject.id,
          userPk: editingMember.userPk,
          syncDownstream: true,
        });
      }
      setMessage(editorMode === 'reassign' ? 'Role assignment updated.' : 'Role assignment added.');
      setEditorMode(null);
      setEditingMember(null);
      await onReload();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save role assignment.');
    } finally {
      setBusy(false);
    }
  };

  const removeAssignment = async (member: ProjectMember) => {
    if (!selectedProject) return;
    if (!window.confirm(`Remove ${member.username || member.email} from ${selectedProject.name}?`)) {
      return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const token = authClient.getToken();
      if (!token) throw new Error('You must be signed in.');
      await deleteProjectMembershipV2(token, {
        projectId: selectedProject.id,
        userPk: member.userPk,
        syncDownstream: true,
      });
      setMessage('Role assignment removed.');
      await onReload();
    } catch (removeError) {
      setError(removeError instanceof Error ? removeError.message : 'Failed to remove role assignment.');
    } finally {
      setBusy(false);
    }
  };

  const saveServices = async () => {
    if (!selectedProject) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const token = authClient.getToken();
      if (!token) throw new Error('You must be signed in.');
      await updateAdminProjectServices(selectedProject.id, services, token);
      const result = await reconcileAdminProject(selectedProject.id, token);
      setMessage(
        result.runState === 'completed'
          ? 'Project services saved and reconciled.'
          : `Project services saved; reconciliation status: ${result.runState || 'started'}.`,
      );
      await onReload();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to update project services.');
    } finally {
      setBusy(false);
    }
  };

  const left = (
    <div className="project-access-directory">
      <div className="project-access-directory-header">
        <h5>Projects</h5>
        {canCreate ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setMode('create');
              setEditorMode(null);
              setError('');
              setMessage('');
            }}
          >
            Add project
          </button>
        ) : null}
      </div>
      {loading ? (
        <p className="tree-dashboard-muted">Loading projects…</p>
      ) : projects.length === 0 ? (
        <div className="project-access-empty-list">
          <p className="tree-dashboard-muted">
            {canCreate
              ? 'No projects in this ecosystem yet.'
              : 'No projects are visible in this ecosystem.'}
          </p>
          {canCreate ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setMode('create');
                setEditorMode(null);
                setError('');
                setMessage('');
              }}
            >
              Add project
            </button>
          ) : null}
        </div>
      ) : (
        <ul className="admin-orch-list project-access-project-list" aria-label="Projects">
          {projects.map((project) => (
            <li key={project.id}>
              <button
                type="button"
                className={`admin-orch-list-row${mode === 'details' && project.id === selectedProjectId ? ' is-selected' : ''}`}
                onClick={() => {
                  setSelectedProjectId(project.id);
                  setMode('details');
                }}
              >
                <strong>{project.name || project.id}</strong>
                <span>{project.id}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  let right;
  if (mode === 'create') {
    right = (
      <div className="project-access-details">
        <div className="project-access-detail-header">
          <div>
            <h5>Add project</h5>
            <p className="tree-dashboard-muted">Create a project and select its platform services.</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => setMode('details')}>
            Cancel
          </button>
        </div>
        <CreateProjectOrchestrationWizard
          initialEcosystemId={ecosystemId}
          initialParentProjectId={focusProjectId || ''}
          onProjectCreated={async (projectId) => {
            await onReload();
            setSelectedProjectId(projectId);
            setMode('details');
          }}
        />
      </div>
    );
  } else if (!selectedProject) {
    right = (
      <div className="project-access-details project-access-empty">
        {canCreate ? (
          <>
            <p className="tree-dashboard-muted">
              This ecosystem has no projects yet. Create one to assign roles and services.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setMode('create');
                setEditorMode(null);
              }}
            >
              Add project
            </button>
          </>
        ) : (
          <p className="tree-dashboard-muted">Select a project to view its details.</p>
        )}
      </div>
    );
  } else {
    const canManageRoles = Boolean(selectedProject.capabilities?.canManageRoles);
    const canManageServices = Boolean(selectedProject.capabilities?.canManageServices);
    const grantableRoles = selectedProject.capabilities?.grantableRoles || [];
    right = (
      <div className="project-access-details">
        <div className="project-access-detail-header">
          <div>
            <h5>{selectedProject.name || selectedProject.id}</h5>
            <p className="tree-dashboard-muted">
              {selectedProject.description || selectedProject.id}
            </p>
          </div>
          {!canManageRoles && !canManageServices ? (
            <span className="admin-status-badge status-unknown" title={selectedProject.capabilities?.reason || ''}>
              View only
            </span>
          ) : null}
        </div>

        {error ? <div className="settings-message settings-error">{error}</div> : null}
        {message ? <div className="settings-message settings-success">{message}</div> : null}

        <section className="project-access-section">
          <div className="project-access-section-heading">
            <h6>Role assignments</h6>
            {canManageRoles ? (
              <button type="button" className="btn btn-primary" disabled={busy} onClick={beginAdd}>
                Add
              </button>
            ) : null}
          </div>
          <div className="project-role-table-wrap">
            <table className="project-role-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Realm username</th>
                  <th>Sync</th>
                  {canManageRoles ? <th className="project-role-actions-column">Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {selectedProject.members.length === 0 ? (
                  <tr>
                    <td colSpan={canManageRoles ? 4 : 3}>No explicit role assignments.</td>
                  </tr>
                ) : (
                  selectedProject.members.map((member) => (
                    <tr key={member.userPk}>
                      <td>{member.role}</td>
                      <td title={member.email}>{member.username || member.email || member.userPk}</td>
                      <td title={projectionSummary(member)}>{projectionSummary(member)}</td>
                      {canManageRoles ? (
                        <td className="project-role-row-actions">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={busy || !grantableRoles.includes(member.role)}
                            title={!grantableRoles.includes(member.role) ? 'This role is at or above your grant ceiling.' : ''}
                            onClick={() => beginReassign(member)}
                          >
                            Reassign
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={busy || !grantableRoles.includes(member.role)}
                            title={!grantableRoles.includes(member.role) ? 'This role is at or above your grant ceiling.' : ''}
                            onClick={() => void removeAssignment(member)}
                          >
                            Remove
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {canManageRoles && editorMode ? (
            <form className="project-role-editor" onSubmit={saveAssignment}>
              <RealmUserPicker
                key={pickerKey}
                id={`project-role-user-${selectedProject.id}`}
                label={editorMode === 'reassign' ? 'Assign to realm user' : 'Realm user'}
                value={selectedUser}
                disabled={busy}
                allowEmailFallback
                onChange={(value, query) => {
                  setSelectedUser(value);
                  setUserQuery(query);
                }}
              />
              <label className="admin-orch-field">
                <span>Role</span>
                <select value={role} disabled={busy} onChange={(event) => setRole(event.target.value)}>
                  {grantableRoles.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
              <div className="admin-create-user-actions">
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Saving…' : editorMode === 'reassign' ? 'Save reassignment' : 'Add assignment'}
                </button>
                <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setEditorMode(null)}>
                  Cancel
                </button>
              </div>
            </form>
          ) : null}
        </section>

        <section className="project-access-section">
          <PlatformServicesChecklist
            idPrefix={`project-service-${selectedProject.id}`}
            packageCatalog={selectedProject.packageCatalog}
            availableServices={resolveInstalledProjectServices(
              selectedProject.availableServices,
              integration.installedServices,
            )}
            realmServices={selectedProject.realmServices}
            ecosystemServices={selectedProject.ecosystemServices}
            canAddRealmServices={Boolean(
              selectedProject.capabilities?.canAddRealmServices
            )}
            canAddEcosystemServices={Boolean(
              selectedProject.capabilities?.canAddEcosystemServices
            )}
            selectedServices={services}
            readOnly={!canManageServices}
            onChange={setServices}
          />
          {canManageServices ? (
            <>
              <p className="tree-dashboard-muted">
                Disabling a service stops future reconciliation; it does not delete existing downstream data.
              </p>
              <div className="admin-create-user-actions">
                <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void saveServices()}>
                  {busy ? 'Saving…' : 'Save & reconcile services'}
                </button>
              </div>
            </>
          ) : (
            <p className="tree-dashboard-muted">Service configuration is read-only for your current role.</p>
          )}
        </section>
      </div>
    );
  }

  return (
    <AdminOrchSplitPane
      className="project-access-split"
      left={left}
      right={right}
      defaultLeftWidth="50%"
      resizeLabel="Resize project list and details"
    />
  );
}
